import { useFormik } from "formik";
import * as Yup from "yup";
import { createAdminReview, updateAdminReview } from "../../../shared/api/reviews.services";
import { slugify } from "../../../shared/lib/slugify";
import toast from "../../../shared/lib/toast";

const validationSchema = Yup.object({
  id: Yup.string().required("ID необхідний"),
  slug: Yup.string().required("Slug необхідний"),
  quote: Yup.object({
    ua: Yup.string().required("Цитата українською необхідна"),
    en: Yup.string().required("Quote in English is required"),
  }),
  author: Yup.object({
    name: Yup.string(),
    subtitle: Yup.string(),
    avatarUrl: Yup.string(),
  }),
  media: Yup.object({
    kind: Yup.string(),
    url: Yup.string(),
    thumbnailUrl: Yup.string(),
    alt: Yup.object({
      ua: Yup.string(),
      en: Yup.string(),
    }),
    durationSec: Yup.number(),
  }),
  sortOrder: Yup.number(),
  isFeatured: Yup.boolean(),
  isActive: Yup.boolean(),
});

const normalizeToLocalized = (val) => {
  if (typeof val === 'string') {
    return { ua: val, en: val };
  }
  if (typeof val === 'object' && val !== null && (val.ua !== undefined || val.en !== undefined)) {
    return val;
  }
  return { ua: '', en: '' };
};

const normalizeString = (val) => {
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null) {
    return val.ua || val.en || '';
  }
  return '';
};

// Адреса відгуку з імені автора й початку цитати.
// Раніше тут стояв фільтр /[^a-z0-9\s-]/, а \w у JS — лише латиниця: з
// «Тест» виходив порожній slug, форма мовчки відмовлялась зберігати, і
// кнопка «Додати» виглядала зламаною. Тепер кирилиця транслітерується.
const buildReviewIds = (authorName, quoteText) => {
  const author = slugify(authorName).slice(0, 30);
  const quote = slugify(String(quoteText || '').trim().split(/\s+/).slice(0, 3).join(' ')).slice(0, 30);
  const slug = [author, quote].filter(Boolean).join('-');

  if (!slug) {
    return { id: '', slug: '' };
  }

  return { id: `review-${slug}`, slug };
};

const buildInitialValues = (data) => {
  if (!data) {
    return {
      id: "",
      slug: "",
      quote: { ua: "", en: "" },
      author: { name: "", subtitle: "", avatarUrl: "" },
      media: { kind: "video", url: "", thumbnailUrl: "", alt: { ua: "", en: "" }, durationSec: 0 },
      sortOrder: 0,
      isFeatured: false,
      isActive: true,
    };
  }

  return {
    id: normalizeString(data.id),
    slug: normalizeString(data.slug),
    quote: normalizeToLocalized(data.quote),
    author: {
      name: normalizeString(data.author?.name) || "",
      subtitle: normalizeString(data.author?.subtitle) || "",
      avatarUrl: data.author?.avatarUrl || "",
    },
    media: {
      kind: data.media?.kind || "video",
      url: data.media?.url || "",
      thumbnailUrl: data.media?.thumbnailUrl || "",
      alt: normalizeToLocalized(data.media?.alt),
      durationSec: data.media?.durationSec || 0,
    },
    sortOrder: data.sortOrder || 0,
    isFeatured: data.isFeatured || false,
    isActive: data.isActive !== false,
  };
};

const useReviewForm = (reviewId, initialData, isEditMode) => {
  const formik = useFormik({
    initialValues: buildInitialValues(initialData),
    validationSchema,
    onSubmit: async (values) => {
      try {
        let response;
        if (isEditMode && reviewId) {
          response = await updateAdminReview(reviewId, values);
        } else {
          response = await createAdminReview(values);
        }

        if (response?.error) {
          toast.error(response.message || "Помилка при збереженні");
          return;
        }

        toast.success(isEditMode ? "Відгук оновлено!" : "Відгук створено!");
      } catch (error) {
        console.error("Error saving review:", error);
        toast.error("Помилка при збереженні відгуку");
      }
    },
  });

  return formik;
};

export { buildReviewIds, useReviewForm };
